import React from 'react';
import { Link } from 'react-router-dom';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cva } from 'class-variance-authority';
import cn from '../utils/cn';

const menuItem = cva(
  'flex w-full items-center gap-2.5 rounded px-3 py-2 border-none bg-transparent text-left text-sm font-medium no-underline cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      destructive: {
        false: 'text-foreground hover:bg-accent [&_i]:text-muted-foreground',
        true: 'text-red-600 hover:bg-red-500/10 [&_i]:text-red-600',
      },
    },
    defaultVariants: { destructive: false },
  },
);

type KoobMenuItemProps = {
  /** FontAwesome class string, e.g. "fa-regular fa-copy". */
  icon?: string;
  /** Renders a react-router Link instead of a button. */
  to?: React.ComponentProps<typeof Link>['to'];
  /** Router state forwarded to the Link. */
  state?: unknown;
  onClick?: React.MouseEventHandler;
  destructive?: boolean;
  /** Button rows only — a `to` row cannot be disabled. */
  disabled?: boolean;
  /** Rows inside a clickable table row opt in so the click doesn't bubble. */
  stopPropagation?: boolean;
  children: React.ReactNode;
  'data-testid'?: string;
};

export const KoobMenuItem = React.forwardRef<
  HTMLButtonElement & HTMLAnchorElement,
  KoobMenuItemProps
>(
  (
    {
      icon,
      to,
      state,
      onClick,
      destructive,
      disabled,
      stopPropagation,
      children,
      ...props
    },
    ref,
  ) => {
    const handleClick: React.MouseEventHandler = (event) => {
      if (stopPropagation) event.stopPropagation();
      onClick?.(event);
    };
    const content = (
      <>
        {icon && <i className={cn('shrink-0 text-base', icon)} />}
        {/* real <div> so e2e text lookups target the item, not the portal */}
        <div className="min-w-0">{children}</div>
      </>
    );

    return (
      <PopoverPrimitive.Close asChild>
        {to != null ? (
          <Link
            ref={ref}
            to={to}
            state={state}
            className={menuItem({ destructive })}
            onClick={handleClick}
            {...props}
          >
            {content}
          </Link>
        ) : (
          <button
            ref={ref}
            type="button"
            className={menuItem({ destructive })}
            onClick={handleClick}
            disabled={disabled}
            {...props}
          >
            {content}
          </button>
        )}
      </PopoverPrimitive.Close>
    );
  },
);
KoobMenuItem.displayName = 'KoobMenuItem';

/** Radix Popover Portal+Content shell shared by all Koob dropdown menus. */
export const KoobMenu = ({
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      align="end"
      sideOffset={4}
      className="z-popover min-w-44 rounded-md border border-solid border-border bg-card p-1 shadow-md"
      {...props}
    >
      {children}
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
);
